import React from "react";
import { Box, Typography, Stack, Grid } from "@mui/material";
import { formatDecimal } from "../../utils/numberFormat";

export default function GenericBars({
  projects,
  title,
  forPdf = false,
  scaleMode = "auto",
  logThreshold = 50,
  minBarPx = 2,
}) {
  const palette = ["#7C3AED", "#22C55E", "#3B82F6"];
  const colorForIndex = (i) => palette[i % palette.length];

  const METRIC_GROUPS = [
    {
      group: "Health",
      metrics: [
        { key: "moreActive", label: "Number of people more active", unit: "people", valuePath: "projectInfo.numberOfPeopleMoreActive", decimals: 0 },
        { key: "lifeYearsGained", label: "Life years gained", unit: "years", valuePath: "projectInfo.lifeYearsGained", decimals: 2 },
        { key: "deathsAverted", label: "Deaths averted", unit: "deaths", valuePath: "projectInfo.deathsAverted", decimals: 0 },
        { key: "lifeExpectancyAtBirth", label: "Life expectancy at birth gained", unit: "years", valuePath: "projectInfo.lifeExpectancyAtBirth", decimals: 2 },
      ],
    },
    {
      group: "Emissions",
      metrics: [
        { key: "co2Reduced", label: "CO2 reduced", unit: "tonnes/year", valuePath: "projectInfo.co2reduced", decimals: 2 },
        { key: "noxReduced", label: "NOx reduced", unit: "kilograms/year", valuePath: "projectInfo.noxReduced", decimals: 2 },
        { key: "so2Reduced", label: "SO2 reduced", unit: "kilograms/year", valuePath: "projectInfo.so2reduced", decimals: 2 },
        { key: "pm10Reduced", label: "PM10 reduced", unit: "kilograms/year", valuePath: "projectInfo.pm10reduced", decimals: 2 },
        { key: "pm25Reduced", label: "PM2.5 reduced", unit: "kilograms/year", valuePath: "projectInfo.pm25reduced", decimals: 2 },
      ],
    },
  ];

  /* ---------- SEM return antes dos hooks ---------- */
  const safeGroupTitle = typeof title === "string" ? title.toLowerCase() : "";
  const selectedGroup = METRIC_GROUPS.find((g) => g.group.toLowerCase() === safeGroupTitle);
  const hasGroup = !!selectedGroup;

  /* ---------- Helpers ---------- */
  const getByPath = (obj, path) =>
    path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);

  const getMetricValue = (project, metric) => {
    const raw = metric.valueFn ? metric.valueFn(project) : getByPath(project, metric.valuePath);
    return Number(raw);
  };

  const toBarValue = (v) => Math.abs(Number(v) || 0);

  const formatMetricValue = (value, decimals = 2) =>
    Number.isFinite(Number(value))
      ? formatDecimal(value, decimals)
      : "--";

  /* ---------- ESCALA (linear/log) - Hook NÃO condicional ---------- */
  const scalePerMetric = React.useMemo(() => {
    if (!hasGroup) return {};

    const result = {};

    for (const m of selectedGroup.metrics) {
      let max = 0;
      let minPositive = Infinity;

      for (const p of projects) {
        const v = toBarValue(getMetricValue(p, m));

        if (v > max) max = v;
        if (v > 0 && v < minPositive) minPositive = v;
      }

      if (max === 0) {
        result[m.key] = { mode: "linear", max: 1, epsilon: 1e-6 };
        continue;
      }

      const range = minPositive === Infinity ? max : max / Math.max(minPositive, 1e-12);
      const useLog = scaleMode === "log" || (scaleMode === "auto" && range >= logThreshold);

      const epsilon = minPositive === Infinity ? 1e-6 : Math.min(minPositive / 10, 1e-3);

      result[m.key] = { mode: useLog ? "log" : "linear", max, epsilon };
    }

    return result;
  }, [projects, selectedGroup, scaleMode, logThreshold, hasGroup]);

  /* ---------- Converter valor → percentagem ---------- */
  const pctFromScale = (raw, scale) => {
    const v = toBarValue(raw);
    const max = scale.max || 1;

    if (scale.mode === "log") {
      const eps = scale.epsilon;
      const num = Math.log(v + eps) - Math.log(eps);
      const den = Math.log(max + eps) - Math.log(eps);
      return Math.max(0, Math.min(100, (num / den) * 100));
    }

    return Math.max(0, Math.min(100, (v / max) * 100));
  };

  /* ---------- Render (SEM return antes dos hooks) ---------- */
  return (
    <Box sx={{ width: "100%", mt: forPdf ? 2 : 6, pageBreakInside: "avoid" }}>
      {!hasGroup && (
        <Typography>Nenhuma métrica encontrada para {title}</Typography>
      )}

      {hasGroup && (
        <Grid container spacing={4}>
          {selectedGroup.metrics.map((m) => {
            const scale = scalePerMetric[m.key];

            return (
              <Grid item xs={12} md={6} key={m.key}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 1 }}>
                  {m.label}{" "}
                  <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                    ({scale.mode})
                  </Typography>
                </Typography>

                <Stack spacing={2}>
                  {projects.map((project, idx) => {
                    const raw = getMetricValue(project, m);
                    const pct = pctFromScale(raw, scale);
                    const color = colorForIndex(idx);

                    const hasValue = Number.isFinite(raw) && raw !== 0;
                    const width = pct === 0 && hasValue ? `${minBarPx}px` : `${pct}%`;

                    return (
                      <Stack key={project.projectName} spacing={0.5}>
                        <Typography
                          variant="subtitle1"
                          sx={{ color:  colorForIndex(idx), fontWeight: 500, lineHeight: 1 }}
                        >
                          {project.projectName}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%", marginTop:"0px !important" }}>
                          <Box sx={{ flexGrow: 1, maxWidth: "calc(100% - 140px)", mt: 0 }}>
                            <Box
                              sx={{
                                height: "14px",
                                width,
                                backgroundColor: `${color}60`,
                                transition: "width 0.3s ease",
                              }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color,
                              minWidth: 140,
                              whiteSpace: "nowrap",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatMetricValue(raw, m.decimals)} {m.unit}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}